import { useCallback, useEffect, useRef, useState } from "react";

/** 侧栏展开宽度（与各子应用 .mouse-menu / .keyboard-menu 一致） */
export const SIDEBAR_WIDTH_EXPANDED = 224;
/** 侧栏折叠宽度 */
export const SIDEBAR_WIDTH_COLLAPSED = 56;
/** 侧栏 flyout 与侧栏右缘的水平间距（left = 侧栏宽度 + GAP） */
export const SIDEBAR_FLYOUT_GAP = 4;

/** 侧栏 flyout 相对锚点右缘的水平偏移（未使用 fixedLeft 时的兜底） */
export const SIDEBAR_FLYOUT_OFFSET_X = 0;

export function getSidebarFlyoutLeft(collapsed: boolean) {
  return (
    (collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH_EXPANDED) +
    SIDEBAR_FLYOUT_GAP
  );
}

type FlyoutPosition = { left: number; top: number };

type UseAnchoredFlyoutOptions = {
  getAnchor: () => HTMLElement | null;
  /** 点击打开等强制展示（如 modal-active） */
  forceOpen?: boolean;
  disabled?: boolean;
  /** 为 false 时不绑定悬停监听（如侧栏未折叠） */
  active?: boolean;
  offsetX?: number;
  offsetY?: number;
  /** 固定 left（如侧栏宽度 + 间距），优先于锚点 right + offsetX */
  fixedLeft?: number;
  /** 离开锚点后延迟关闭（ms），需留出移入 Portal 的时间 */
  hideDelay?: number;
};

export function useAnchoredFlyout({
  getAnchor,
  forceOpen = false,
  disabled = false,
  active = true,
  offsetX = SIDEBAR_FLYOUT_OFFSET_X,
  offsetY = 0,
  fixedLeft,
  hideDelay = 250,
}: UseAnchoredFlyoutOptions) {
  const [hoverOpen, setHoverOpen] = useState(false);
  const [pos, setPos] = useState<FlyoutPosition>({ left: 0, top: 0 });
  const hideTimerRef = useRef<number>();
  const portalRef = useRef<HTMLDivElement>(null);

  const updatePos = useCallback(() => {
    const anchor = getAnchor();
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    setPos({
      left: fixedLeft ?? rect.right + offsetX,
      top: rect.top + offsetY,
    });
  }, [getAnchor, fixedLeft, offsetX, offsetY]);

  const visible = !disabled && (forceOpen || hoverOpen);

  const clearHideTimer = useCallback(() => {
    window.clearTimeout(hideTimerRef.current);
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(
      () => setHoverOpen(false),
      hideDelay
    );
  }, [clearHideTimer, hideDelay]);

  const openFlyout = useCallback(() => {
    clearHideTimer();
    if (disabled) return;
    updatePos();
    setHoverOpen(true);
  }, [clearHideTimer, disabled, updatePos]);

  const isMovingToPortal = useCallback((relatedTarget: EventTarget | null) => {
    if (!(relatedTarget instanceof Node)) return false;
    return portalRef.current?.contains(relatedTarget) ?? false;
  }, []);

  useEffect(() => {
    if (!active) return;
    const anchor = getAnchor();
    if (!anchor) return;

    const onEnter = () => openFlyout();

    const onLeave = (e: MouseEvent) => {
      if (isMovingToPortal(e.relatedTarget)) return;
      scheduleHide();
    };

    anchor.addEventListener("mouseenter", onEnter);
    anchor.addEventListener("mouseleave", onLeave);
    return () => {
      anchor.removeEventListener("mouseenter", onEnter);
      anchor.removeEventListener("mouseleave", onLeave);
      clearHideTimer();
    };
  }, [
    active,
    getAnchor,
    openFlyout,
    scheduleHide,
    clearHideTimer,
    isMovingToPortal,
  ]);

  useEffect(() => {
    if (!active) return;
    updatePos();
  }, [active, getAnchor, updatePos]);

  useEffect(() => {
    if (!visible) return;
    updatePos();
    const onUpdate = () => updatePos();
    window.addEventListener("resize", onUpdate);
    window.addEventListener("scroll", onUpdate, true);
    return () => {
      window.removeEventListener("resize", onUpdate);
      window.removeEventListener("scroll", onUpdate, true);
    };
  }, [visible, updatePos]);

  const portalHoverHandlers = {
    onMouseEnter: openFlyout,
    onMouseLeave: (e: React.MouseEvent) => {
      const anchor = getAnchor();
      if (
        anchor &&
        e.relatedTarget instanceof Node &&
        anchor.contains(e.relatedTarget)
      ) {
        return;
      }
      scheduleHide();
    },
  };

  return { visible, pos, portalRef, portalHoverHandlers, updatePos };
}
